import type { ClusterMatrix, RankingRules } from '../../data/schemas';
import { getQuestionResultCopy } from '../../data/questionResultCopy';
import { QUESTION_IDS } from '../../types/ids';
import type { DiagnosticInsight, EvaluationContext } from '../../types/result';
import { getPrimaryClusterForQuestion, getRelatedClustersForQuestion } from './primaryCluster';

/**
 * Ermittelt Engpässe (FR-INSIGHT-002): Fragewert = 0 und (zugehöriger Cluster höchstens 44 % ODER
 * fachlich besonders kritische Frage). Kritische Fragen erhalten einen Prioritätsbonus (FR-INSIGHT-003),
 * damit z. B. ein fehlender Verkaufsweg vor einem begrenzten Themenvorrat erscheint.
 */
export function detectWeakPoints(
  context: EvaluationContext,
  matrix: ClusterMatrix,
  rankingRules: RankingRules,
): DiagnosticInsight[] {
  const candidates: DiagnosticInsight[] = [];
  const criticalIds = new Set(rankingRules.weakPoints.businessCriticalQuestionIds);

  for (const questionId of QUESTION_IDS) {
    const answer = context.answers[questionId];
    if (!answer || answer.score !== rankingRules.weakPoints.maximumQuestionScore) continue;

    const primaryCluster = getPrimaryClusterForQuestion(questionId, matrix);
    const clusterResult = context.clusters[primaryCluster];
    const isCritical = criticalIds.has(questionId);
    const clusterIsWeak =
      !!clusterResult &&
      clusterResult.percentage <= rankingRules.weakPoints.maximumClusterPercentage;

    if (!isCritical && !clusterIsWeak) continue;

    const copy = getQuestionResultCopy(questionId, answer.score);
    const importance =
      copy.defaultImportance +
      (isCritical ? rankingRules.weakPoints.businessCriticalPriorityBonus : 0);

    candidates.push({
      id: `weak_point_${questionId}`,
      type: 'weak_point',
      questionId,
      title: copy.title,
      summary: copy.summary,
      detail: copy.detail,
      importance,
      sourceQuestionIds: [questionId],
      relatedClusterIds: getRelatedClustersForQuestion(questionId, matrix),
    });
  }

  candidates.sort((a, b) => b.importance - a.importance);
  return candidates.slice(0, rankingRules.weakPoints.maximumItems);
}
